import React, { useState } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { SOURCE_STEP, FORM_STEP } from '@chegg-wt/core/constants/citationFlow'
import { FormTypes, FormType } from '@chegg-wt/core/constants/form'
import { JOURNAL } from '@chegg-wt/core/constants/journalTypes'
import { CitationFlowStep } from '@chegg-wt/core/types/citation-flow'
import { Source, SourceType, JournalType } from '@chegg-wt/core/types/core'
import { ValidatorState } from '@chegg-wt/core/state/citation/types'
import { getAutociteSource } from '@chegg-wt/core/api/source'
import { Config } from '@chegg-wt/core/config'
import {
  origins,
  Region,
} from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import useCitationFlowStepChange from '@chegg-wt/core/hooks/useCitationFlowStepChange'
import useSearchSources from '@chegg-wt/core/hooks/useSearchSources'
import {
  toSearchResult,
  SyntheticLocationChangePayload,
  syntheticLocationChange,
  userClick,
} from '@chegg-wt/core/state/instrumentation/actions'
import { CROSSREF_RESULT_COUNT } from '@chegg-wt/core/state/optimizely/constants'
import { useSafeOptimizelyFeature } from '@chegg/optimizely-sdk'
import { AppState } from '@chegg-wt/core/state'
import { autociteApiVersionSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { softLoggedInSelector, jwtSelector } from '@chegg-wt/core/state/user/selectors'
import SEOTitle from '../SEOTitle'
import CitationForm from './CitationForm'
import CitationSearch from './CitationSearch'

const getFormType = (isEdit: boolean, hasSearched: boolean): FormType => {
  if (isEdit) return FormTypes.EDIT
  if (!hasSearched) return FormTypes.MANUAL
  return FormTypes.NEW
}

interface DispatchProps {
  syntheticLocationChange: (payload: SyntheticLocationChangePayload) => void
  onCreateManualCitationClickCTA: (region: Region) => void
}

interface StateProps {
  autociteApiVersion: string
  softLoggedIn: boolean
  cheggUserUuid?: string | null
}

export interface CitationFlowProps {
  step: CitationFlowStep
  citationStyle: string
  sourceType: SourceType
  initialSearchQuery?: string
  citation?: any
  projectId: string
  isEdit: boolean
  onChangeSourceType: (sourceType: SourceType) => void
  onSelectSource: (source?: Source) => void
  onBack: () => void
  onComplete: (citation: any, status: ValidatorState, projectId?: string) => void
}

const CitationFlow: React.FC<CitationFlowProps & StateProps & DispatchProps> = ({
  step,
  sourceType,
  initialSearchQuery,
  citationStyle: style,
  citation,
  projectId,
  isEdit,
  autociteApiVersion,
  softLoggedIn,
  cheggUserUuid,
  onChangeSourceType,
  onSelectSource,
  onBack,
  onComplete,
  syntheticLocationChange,
  onCreateManualCitationClickCTA,
}) => {
  useCitationFlowStepChange({ step, sourceType, onChangeSourceType, onSelectSource })
  const { isEnabled: crossrefResultCountEnabled } = useSafeOptimizelyFeature(
    CROSSREF_RESULT_COUNT
  )

  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery || '')
  const [journalType, setJournalType] = useState<JournalType>(JOURNAL)

  const { sources, loading, error, reset, invalidSearch } = useSearchSources({
    sourceType,
    searchQuery,
    journalType,
    autociteApiVersion,
    clientCommonFields: {
      softLoggedIn,
      cheggUserUuid,
    },
    skip: step !== SOURCE_STEP,
    crossrefResultCountEnabled,
  })
  const { siteName } = Config

  const handleChangeSourceTypeSearch = (sourceType: SourceType) => {
    if (sourceType.manual) {
      setSearchQuery('')
      setJournalType(JOURNAL)
      reset()
    }
    onChangeSourceType(sourceType)
  }

  const handleSelectSource = async (source?: Source) => {
    const newSource = source?.data
      ? await getAutociteSource(source, autociteApiVersion, {
          softLoggedIn,
          cheggUserUuid,
        })
      : source
    onSelectSource(newSource)
  }

  const handleSearchQuery = (searchQuery: string) => {
    setSearchQuery(searchQuery)
    syntheticLocationChange({ routeName: toSearchResult })
  }

  const handleManualCiteClick = (region: Region) => {
    onCreateManualCitationClickCTA(region)
    handleSelectSource()
  }

  switch (step) {
    case SOURCE_STEP: {
      return (
        <>
          <SEOTitle title={`Find your source | ${siteName}`} />
          <CitationSearch
            searchText={searchQuery}
            citationStyle={style}
            sourceType={sourceType}
            initialSearchQuery={searchQuery}
            loading={loading}
            error={error}
            sources={loading ? [] : sources}
            journalType={journalType}
            invalidSearch={invalidSearch}
            onChangeSourceType={handleChangeSourceTypeSearch}
            onChangeSearchQuery={handleSearchQuery}
            onChangeJournalType={setJournalType}
            onSelectSource={handleSelectSource}
            onManualCiteClick={handleManualCiteClick}
          />
        </>
      )
    }
    case FORM_STEP: {
      return (
        <>
          <SEOTitle title={`Finalize details | ${siteName}`} />
          <CitationForm
            citationStyle={style}
            sourceType={sourceType}
            citation={citation}
            projectId={projectId}
            formType={getFormType(isEdit, !!sources)}
            onChangeSourceType={onChangeSourceType}
            onBack={onBack}
            onComplete={onComplete}
          />
        </>
      )
    }
    default: {
      return null
    }
  }
}

const mapStateToProps = (state: AppState) => ({
  autociteApiVersion: autociteApiVersionSelector(state),
  softLoggedIn: softLoggedInSelector(state),
  cheggUserUuid: jwtSelector(state)?.cheggUserUuid,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  syntheticLocationChange: (payload: SyntheticLocationChangePayload) =>
    dispatch(syntheticLocationChange(payload)),
  onCreateManualCitationClickCTA: (region: Region) =>
    dispatch(userClick({ origin: origins.createManualCitationCTA, region })),
})

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(CitationFlow)
